import { Mui, useTheme } from "@leap4/frontend";
import React, { useMemo, useState } from "react";
import LineChart, { IChartPoint, IChartSeries } from "../../../common/lineChart";
import { IReport } from "../report.interface";
import {
    eventColumnKeyFromEventName,
    eventNameFromPredictedMetricKey,
    PREDICTION_BAND_COLOR,
    predictionErrorBand,
    isPredictedMetricKey,
} from "../reports";

const { Box, MenuItem, Paper, Select, Stack, Typography, FormControl, InputLabel, darken, lighten } = Mui;

/** One point of the prediction series, as the backend returns it. */
export interface IPredictionSeriesPoint {
    pixelDate: string;
    basis: string;
    expectedTotal: number;
    standardError?: number | null;
    /** Credit observed so far. Null for forecast dates, which have no observations at all. */
    soFar?: number | null;
}

/** One event's series, kept apart so each event gets its own colour, label and maturing window. */
export interface IPredictionEventSeries {
    event: string;
    points: IPredictionSeriesPoint[];
}

interface IProps {
    rows: Array<Record<string, any>>;
    report: IReport;
    /** Column name to label, as resolved for the grid, so the chart names things the same way. */
    columnLabels: Map<string, string>;
    /**
     * Predicted points, if the report requested them. Report rows cannot supply the forecast half —
     * those dates have no impressions yet, so no row exists for them.
     */
    predictionSeries?: IPredictionEventSeries[];
    /** Shown in place of the predicted lines when a series could not be fetched. */
    predictionError?: string | null;
}

/**
 * The event as the grid names it, with the metric's "Predicted" prefix taken off: the two lines are
 * one metric in two states, and naming them "Purchase (actual)" and "Purchase (predicted)" sorts them
 * together in the legend where "Actual (purchase)" and "Predicted Purchase" sat a line apart.
 */
const eventLabelFor = (
    event: string,
    predictedColumn: string,
    metrics: IReport["metrics"],
    columnLabels: Map<string, string>,
): string => {
    // The picked metric first, the grid column second. The grid only carries a label once the report
    // produced that column, and the chart draws the series whether or not it did.
    const picked = metrics.find(
        (m) => isPredictedMetricKey(m.eventType) && eventNameFromPredictedMetricKey(m.eventType) === event,
    );
    const label = picked?.label ?? (predictedColumn ? columnLabels.get(predictedColumn) : undefined);
    return (label ?? event).replace(/^Predicted\s*/i, "").trim() || event;
};

const WEEKDAY_NAMES = [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/**
 * Axis labels stay short because they sit under one another; the tooltip has room for the day name,
 * which is what makes a weekly rhythm in the numbers readable.
 */
const pointMeta = (date: string) => ({
    label: date.slice(5),
    major: date.endsWith("-01"),
    tooltipLabel: `${date.slice(8, 10)}/${date.slice(5, 7)} ${
        WEEKDAY_NAMES[new Date(`${date}T00:00:00.000Z`).getUTCDay()]
    }`,
});

const DATE_COLUMNS = ["pixel_date", "event_date"];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Every date the report asked for, whether or not a row came back for it. The axis has to come from
 * the range the user picked rather than from the rows: a report whose only numeric columns are
 * predictions has no measured date to seed it, and the chart then collapsed onto the forecast half.
 */
const rangeDates = (from: string | null, to: string | null): string[] => {
    const start = String(from ?? "").slice(0, 10);
    const end = String(to ?? "").slice(0, 10);
    if (!ISO_DATE.test(start) || !ISO_DATE.test(end) || end < start) return [];

    const out: string[] = [];
    const cursor = new Date(`${start}T00:00:00.000Z`);
    const last = Date.parse(`${end}T00:00:00.000Z`);
    while (cursor.getTime() <= last && out.length < 1000) {
        out.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
};

/**
 * Charts a finished report over its date axis. Rows may be grouped by cohort as well as date, so
 * values are summed per date — a chart of a cohort-split report shows the campaign shape, and the
 * grid stays the place to read individual cohorts.
 *
 * Predictions render in three parts because they are not equally trustworthy: measured values solid,
 * projections solid with a confidence band, and the forecast dashed with its own band. A band wider
 * than the value it surrounds is the honest signal that a cohort is too thin to rely on.
 */
const ReportChart = ({
    rows,
    report,
    columnLabels,
    predictionSeries = [],
    predictionError = null,
}: IProps): JSX.Element | null => {
    const theme = useTheme();

    const dateColumn = useMemo(
        () => DATE_COLUMNS.find((c) => rows.some((r) => r[c] != null && String(r[c]).length >= 10)) ?? null,
        [rows],
    );

    const measuredColumns = useMemo(() => {
        if (rows.length === 0) return [];
        const candidate = new Set<string>();
        for (const row of rows) {
            for (const [key, value] of Object.entries(row)) {
                if (DATE_COLUMNS.includes(key)) continue;
                if (typeof value === "number") candidate.add(key);
            }
        }
        return Array.from(candidate);
    }, [rows]);

    // A predicted column shares its units with the prediction series, so it keeps the axis usable.
    const predictedColumns = useMemo(
        () => measuredColumns.filter((c) => c.startsWith("predicted_")),
        [measuredColumns],
    );

    /**
     * An attributed event whose prediction is also drawn is dropped: the predicted series already
     * carries an "actual" line for that event, from the same credit. Both drawn, the pair read as two
     * different figures that happen to agree — and on a normalised chart they sit at different heights,
     * which reads as a discrepancy rather than as the same number twice.
     */
    const duplicatedEventColumns = useMemo(
        () => new Set(predictionSeries.map((s) => eventColumnKeyFromEventName(s.event))),
        [predictionSeries],
    );

    const plainColumns = useMemo(
        () =>
            measuredColumns.filter(
                (c) => !c.startsWith("predicted_") && !duplicatedEventColumns.has(c),
            ),
        [measuredColumns, duplicatedEventColumns],
    );

    const [selected, setSelected] = useState<string>("");
    const [focusedId, setFocusedId] = useState<string | null>(null);

    // Two or more events cannot share one axis: a count and a revenue sum differ by orders of
    // magnitude, and the smaller flattens onto zero. Focusing one is what earns the labels back.
    const normalise = predictionSeries.length > 1;
    const activeColumn =
        selected && measuredColumns.includes(selected)
            ? selected
            : plainColumns[0] ?? predictedColumns[0] ?? "";

    const showPrediction = predictedColumns.length > 0 || predictionSeries.length > 0;

    // Local, because the report's dates are local and the shading has to line up with them.
    const today = useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
            now.getDate(),
        ).padStart(2, "0")}`;
    }, []);

    const { series, maturingFromIndex, maturingToIndex } = useMemo(() => {
        const empty = { series: [] as IChartSeries[], maturingFromIndex: null, maturingToIndex: null };
        // A report with no date dimension still charts its predictions, over the range it asked for.
        if (!dateColumn && predictionSeries.length === 0) return empty;

        const measured = new Map<string, number>();
        if (dateColumn && activeColumn) {
            for (const row of rows) {
                const date = String(row[dateColumn]).slice(0, 10);
                if (date.length < 10) continue;
                const raw = row[activeColumn];
                if (raw === null || raw === undefined || raw === '') continue;
                const value = Number(raw);
                if (Number.isFinite(value)) measured.set(date, (measured.get(date) ?? 0) + value);
            }
        }

        // One bundle per selected event, so each keeps its own cohort sums, colour and label.
        const bundles = predictionSeries.map((eventSeries) => {
            const slug = eventSeries.event
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "_")
                .replace(/^_+|_+$/g, "");
            const column = `predicted_${slug || "unknown"}`;

            const predictedFromRows = new Map<string, number>();
            if (dateColumn) {
                for (const row of rows) {
                    const date = String(row[dateColumn]).slice(0, 10);
                    const raw = row[column];
                    if (date.length < 10 || raw === null || raw === undefined || raw === '') continue;
                    const value = Number(raw);
                    if (Number.isFinite(value)) predictedFromRows.set(date, (predictedFromRows.get(date) ?? 0) + value);
                }
            }

            // Sum across cohorts if the series was fetched at a finer level than the campaign.
            const predicted = new Map<string, { total: number; error: number; basis: string }>();
            const actual = new Map<string, number>();
            for (const point of eventSeries.points) {
                const existing = predicted.get(point.pixelDate);
                const error = point.standardError ?? 0;
                predicted.set(point.pixelDate, {
                    total: (existing?.total ?? 0) + point.expectedTotal,
                    error: (existing?.error ?? 0) + error,
                    basis: point.basis,
                });
                if (point.soFar != null) {
                    actual.set(point.pixelDate, (actual.get(point.pixelDate) ?? 0) + point.soFar);
                }
            }

            return { event: eventSeries.event, column, predicted, actual, predictedFromRows };
        });

        // Deliberately nothing merged in from the rows here: the only per-date column the report
        // carries for this event is the prediction itself, and seeding the actual line from it draws
        // the two lines on top of each other with identical values.

        // The series carries history the report never asked for, so the axis starts where the report
        // does and only the forecast is allowed to extend it past the end. The spine is the picked
        // range rather than the dates that came back, so a gap in the data is a gap in the line.
        const spine = rangeDates(report.startDate, report.endDate);
        const dates = Array.from(
            new Set(
                (spine.length > 0 ? spine : Array.from(measured.keys()).sort()).concat(
                    bundles
                        .flatMap((b) => Array.from(b.predicted.keys()))
                        .filter((d) => !spine[0] || d >= spine[0]),
                ),
            ),
        ).sort();
        if (dates.length === 0) {
            return { series: [] as IChartSeries[], maturingFromIndex: null, maturingToIndex: null };
        }

        const measuredPoints: IChartPoint[] = [];
        // The widest still-open window across the drawn events: hatching the narrowest would leave
        // another event's maturing dates looking settled.
        let firstPredictedIndex: number | null = null;
        // Per event as well, because the windows differ — a 28-day visit beside a 14-day purchase
        // hatches the whole chart, which then says nothing about either. Focusing one narrows the
        // hatching to that event's window, which is the question focusing it asks.
        const firstPredictedByEvent = new Map<string, number>();
        // Impressions stop at today, and past that nothing is accruing yet, so the shading ends here.
        // Keyed off today rather than the last row with a value: a date the report covers but has no
        // data for is still the past, and shading it as the future said the opposite.
        let lastMeasuredIndex: number | null = null;

        dates.forEach((date, i) => {
            const meta = pointMeta(date);
            measuredPoints.push({ ...meta, value: measured.has(date) ? (measured.get(date) as number) : null });
            if (date <= today) lastMeasuredIndex = i;

            for (const bundle of bundles) {
                const p = bundle.predicted.get(date);
                // Where the window is still open, not merely where the series starts: settled dates
                // carry points too now, and hatching those would shade the whole chart.
                if (!p || p.basis === "actual") continue;
                if (firstPredictedIndex === null) firstPredictedIndex = i;
                if (!firstPredictedByEvent.has(bundle.event)) firstPredictedByEvent.set(bundle.event, i);
            }
        });

        const forecastPointsFor = (
            predicted: Map<string, { total: number; error: number; basis: string }>,
        ): IChartPoint[] =>
            dates.map((date, i) => {
                const meta = pointMeta(date);
                const p = predicted.get(date);
                const withBand = (): IChartPoint => ({
                    ...meta,
                    value: p ? p.total : null,
                    note:
                        p && p.error > 0 && p.total > 0
                            ? `± ${Math.round((p.error / p.total) * 100)}%`
                            : undefined,
                    noteColor:
                        p && p.error > 0 && p.total > 0
                            ? PREDICTION_BAND_COLOR[
                                  predictionErrorBand((p.error / p.total) * 100) ?? 'bad'
                              ]
                            : undefined,
                    upper: p && p.error > 0 ? p.total + p.error : null,
                    lower: p && p.error > 0 ? p.total - p.error : null,
                });

                const isForecast = p?.basis === "forecast";
                // Repeat the boundary point on the forecast line so the two join visually.
                return isForecast
                    || (p && dates[i + 1] && predicted.get(dates[i + 1])?.basis === "forecast")
                    ? withBand()
                    : { ...meta, value: null };
            });

        // Separate scales when the magnitudes are far apart: served impressions run to hundreds of
        // thousands while purchases run to hundreds, and on one axis the smaller flattens onto zero.
        const maxOf = (m: Map<string, number>) =>
            Array.from(m.values()).reduce((a, b) => Math.max(a, b), 0);
        const leftMax = maxOf(measured);
        const predictedMax = Math.max(
            0,
            ...bundles.map((b) =>
                Math.max(
                    maxOf(b.predictedFromRows),
                    maxOf(b.actual),
                    ...Array.from(b.predicted.values()).map((p) => p.total),
                    0,
                ),
            ),
        );
        const predictedAxis: "left" | "right" =
            leftMax > 0 && predictedMax > 0 && Math.max(leftMax, predictedMax) / Math.min(leftMax, predictedMax) > 20
                ? "right"
                : "left";

        // Hue identifies the event, tone the basis within it. One colour for both put a measured and a
        // predicted line on top of each other wherever they overlapped, which is exactly where the
        // difference between them matters. The prediction keeps the full-strength hue: it is what the
        // chart is for, and the darker measured line reads as the settled record behind it.
        const actualTone = (color: string): string => darken(color, 0.32);

        // Colour identifies the event, line style the basis within it. Fixed hues rather than the
        // theme's roles: those put two events a few degrees apart, and at band opacity a violet and a
        // red are the same colour. Blue is left out entirely — served impressions already owns it.
        const eventColors = ['#ef5350', '#66bb6a', '#ffca28', '#ab47bc', '#26c6da'];

        const out: IChartSeries[] = [];
        // The label each bundle's lines are named after, so a focused label maps back to its event.
        const labelOf = new Map(
            bundles.map((b) => [
                b.event,
                eventLabelFor(b.event, b.column, report.metrics, columnLabels),
            ]),
        );

        if (measured.size > 0) {
            out.push({
                id: activeColumn,
                label: columnLabels.get(activeColumn) ?? activeColumn,
                color: theme.palette.primary.main,
                points: measuredPoints,
            });
        }

        bundles.forEach((bundle, bundleIndex) => {
            const { predicted, actual, predictedFromRows, column } = bundle;
            // Colour identifies the event; line style still identifies the basis within it.
            const color = eventColors[bundleIndex % eventColors.length];
            const measuredColor = actualTone(color);
            const eventLabel = eventLabelFor(bundle.event, column, report.metrics, columnLabels);

            // A settled date has an answer, so it belongs to the measured line and not the predicted
            // one. Drawing a prediction across dates that finished weeks ago made the two look swapped.
            const isSettled = (date: string) => predicted.get(date)?.basis === "actual";

            if (actual.size > 0) {
                out.push({
                    id: `predicted-actual:${bundle.event}`,
                    label: `${eventLabel} (actual)`,
                    color: measuredColor,
                    axis: predictedAxis,
                    scaleKey: bundle.event,
                    points: dates.map((date) => ({
                        ...pointMeta(date),
                        value: actual.has(date) ? (actual.get(date) as number) : null,
                        major: date.endsWith("-01"),
                    })),
                });
            }

            // Matured and immature dates come from the report rows, so the line is continuous rather
            // than starting where the prediction window does.
            const predictedLabel = `${eventLabel} (predicted)`;

            // The join between measured and predicted: the newest date whose window has closed. Both
            // lines carry it, so they meet at a shared point rather than butting up against each other.
            const lastSettledDate = dates.filter(isSettled).pop() ?? null;

            // Split by whether the interval is narrower than the value. Both halves carry the same
            // label, so the tooltip still shows one prediction row — the split is only a visual signal
            // that the recent end is an extrapolation rather than a reading.
            const predictedPoint = (date: string): IChartPoint => {
                const meta = pointMeta(date);
                const p = predicted.get(date);
                if (p?.basis === "forecast") return { ...meta, value: null };

                // Settled dates are measured, not predicted. The last one is carried anyway so the
                // prediction grows out of the measurement rather than appearing beside it, but it is
                // marked silent: its window is closed, so calling it a prediction would be wrong.
                const isJoin = p?.basis === "actual";
                if (isJoin && date !== lastSettledDate) {
                    return { ...meta, value: null };
                }

                // Series first: it is the projection, and it carries the error the split needs. The row
                // column is the same figure from the same source, kept only for dates the series misses.
                const value = p?.total ?? predictedFromRows.get(date) ?? null;
                const error = p && p.error > 0 ? p.error : null;
                if (value == null) return { ...meta, value: null };


                return {
                    ...meta,
                    value,
                    silent: isJoin,
                    note: error != null && value > 0 ? `± ${Math.round((error / value) * 100)}%` : undefined,
                    noteColor:
                        error != null && value > 0
                            ? PREDICTION_BAND_COLOR[
                                  predictionErrorBand((error / value) * 100) ?? 'bad'
                              ]
                            : undefined,
                    upper: error != null ? value + error : null,
                    lower: error != null ? value - error : null,
                };
            };

            // One line for the whole in-window prediction. How shaky the estimate is, is what the
            // band says; splitting the stroke into a second dash pattern said it twice and made one
            // metric read as two lines meeting.
            const predictedPoints = dates.map((d) => predictedPoint(d));

            out.push({
                id: `predicted-observed:${bundle.event}`,
                label: predictedLabel,
                scaleKey: bundle.event,
                color,
                axis: predictedAxis,
                fadeFrom: measuredColor,
                points: predictedPoints,
            });


            out.push({
                id: `predicted-forecast:${bundle.event}`,
                label: predictedLabel,
                scaleKey: bundle.event,
                color,
                dashed: true,
                axis: predictedAxis,
                points: forecastPointsFor(predicted),
            });
        });

        // The focused line's own window when one is focused, so the hatching answers for the line being
        // read rather than for the widest window on the chart.
        const focusedEvent = focusedId
            ? bundles.find((b) => focusedId.startsWith(`${labelOf.get(b.event) ?? ""} (`))?.event
            : undefined;
        const maturingFrom = focusedEvent
            ? firstPredictedByEvent.get(focusedEvent) ?? firstPredictedIndex
            : firstPredictedIndex;

        return { series: out, maturingFromIndex: maturingFrom, maturingToIndex: lastMeasuredIndex };
    }, [
        rows,
        report.metrics,
        focusedId,
        dateColumn,
        activeColumn,
        predictionSeries,
        columnLabels,
        theme,
        today,
        report.startDate,
        report.endDate,
    ]);

    if (series.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Add Impression Date or Conversion Date to the report to chart it over time.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Over time</Typography>
                {/* Only where it can change something: the report's own metrics are per date, so
                    without a date dimension there is no line to switch between, and with one metric
                    selected there is nothing to switch to. */}
                {dateColumn && plainColumns.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="report-chart-metric">Metric</InputLabel>
                        <Select
                            labelId="report-chart-metric"
                            label="Metric"
                            value={activeColumn}
                            onChange={(event) => setSelected(String(event.target.value))}
                        >
                            {plainColumns.map((column) => (
                                <MenuItem key={column} value={column}>
                                    {columnLabels.get(column) ?? column}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Stack>

            <Box>
                <LineChart
                    series={series}
                    height={300}
                    maturingFromIndex={maturingFromIndex}
                    maturingToIndex={maturingToIndex}
                    futureFromIndex={maturingToIndex}
                    normalise={normalise}
                    focusedId={focusedId}
                    onFocusChange={setFocusedId}
                    maturingNote="Attribution window still open — these impression dates keep accruing conversions, which is why they are predicted rather than measured."
                />
            </Box>

            {!dateColumn && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Predicted campaign totals over the report's date range. The report's own metrics carry
                    no date without an Impression Date dimension, so only the predictions — which bring
                    their own dates — can be drawn over time. Add Impression Date to chart the rest.
                </Typography>
            )}

            {predictionError && (
                <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
                    Prediction series unavailable: {predictionError}
                </Typography>
            )}

            {showPrediction && predictionSeries.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                    Measured up to the last date whose attribution window has closed, predicted after it. The
                    gap between the two lines is what is still expected to arrive; the shaded band and the
                    tooltip's percentage are the margin of error. Predictions read against the right-hand axis
                    when their scale differs.
                </Typography>
            )}
        </Paper>
    );
};

export default ReportChart;
